import Typography from "@mui/material/Typography";
import { OnboardingInputControl } from "ui/molecules/Input";
import { ProofFileInput, acceptRules, IdentityPlaceholderTitle } from "ui/forms/formik-elements/FileInput";
import OnboardingForm from "../../OnboardingForm";
import { FormField, type OnboardingUploadDocumentsProps } from "./types";

const UploadDocuments = ({
    values,
    isSubmitting,
    isValid,
    isBootstrapDataLoading,
    isIncomplete,
    requestedChangeFields,
    bootstrapData,
    setFieldValue,
    setFieldTouched,
    handleChange,
    handleBlur,
    handleSubmit,
    ...form
}: OnboardingUploadDocumentsProps) => (
    <OnboardingForm
        {...form}
        isSubmitting={isSubmitting}
        isValid={isValid}
        isIncomplete={isIncomplete}
        requestedChangeFields={requestedChangeFields}
        handleSubmit={handleSubmit}
    >
        <Typography
            variant='h6'
            color='error'
        >
            "Copy of your license(s)", "Proof of Domain Ownership", "Company structure chart / diagram"
            require multifile upload capability!!!
        </Typography>
        <Typography
            variant='h6'
        >
            Certificate of Incorporation
        </Typography>
        <OnboardingInputControl
            name={FormField.CertificateOfIncorporationFileKey}
            isIncomplete={isIncomplete}
            requestedChangeFields={requestedChangeFields}
            renderInput={({ disabled }) => (
                <ProofFileInput
                    multiple={false}
                    disabled={disabled}
                    name={FormField.CertificateOfIncorporationFileKey}
                    file={values[FormField.CertificateOfIncorporationFileKey]}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    placeholderProps={{
                        titleSlot: (
                            <IdentityPlaceholderTitle />
                        )
                    }}
                    accept={acceptRules.identity}
                />
            )}
        />
        <Typography
            marginTop={2}
            variant='h6'
        >
            Memorandum and Articles of Association
        </Typography>
        <Typography
            variant='caption'
        >
            (or official document that shows shareholders with percentage of shares)
        </Typography>
        <OnboardingInputControl
            name={FormField.MemorandumAndArticlesFileKey}
            isIncomplete={isIncomplete}
            requestedChangeFields={requestedChangeFields}
            renderInput={({ disabled }) => (
                <ProofFileInput
                    multiple={false}
                    disabled={disabled}
                    name={FormField.MemorandumAndArticlesFileKey}
                    file={values[FormField.MemorandumAndArticlesFileKey]}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    placeholderProps={{
                        titleSlot: (
                            <IdentityPlaceholderTitle />
                        )
                    }}
                    accept={acceptRules.identity}
                />
            )}
        />
        <Typography
            marginTop={2}
            variant='h6'
        >
            Company Account Bank Statement
        </Typography>
        <OnboardingInputControl
            name={FormField.CompanyAccountBankStatementFileKey}
            isIncomplete={isIncomplete}
            requestedChangeFields={requestedChangeFields}
            renderInput={({ disabled }) => (
                <ProofFileInput
                    multiple={false}
                    disabled={disabled}
                    name={FormField.CompanyAccountBankStatementFileKey}
                    file={values[FormField.CompanyAccountBankStatementFileKey]}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    placeholderProps={{
                        titleSlot: (
                            <IdentityPlaceholderTitle />
                        )
                    }}
                    accept={acceptRules.identity}
                />
            )}
        />
        <Typography
            marginTop={2}
            variant='h6'
        >
            Company Financial Statement
        </Typography>
        <Typography
            variant='caption'
        >
            Previous fiscal year
        </Typography>
        <OnboardingInputControl
            name={FormField.CompanyFinancialStatementFileKey}
            isIncomplete={isIncomplete}
            requestedChangeFields={requestedChangeFields}
            renderInput={({ disabled }) => (
                <ProofFileInput
                    multiple={false}
                    disabled={disabled}
                    name={FormField.CompanyFinancialStatementFileKey}
                    file={values[FormField.CompanyFinancialStatementFileKey]}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                />
            )}
        />
        <Typography
            marginTop={2}
            variant='h6'
        >
            Bank Reference
        </Typography>
        <OnboardingInputControl
            name={FormField.BankReferenceFileKey}
            isIncomplete={isIncomplete}
            requestedChangeFields={requestedChangeFields}
            renderInput={({ disabled }) => (
                <ProofFileInput
                    multiple={false}
                    disabled={disabled}
                    name={FormField.BankReferenceFileKey}
                    file={values[FormField.BankReferenceFileKey]}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    placeholderProps={{
                        titleSlot: (
                            <IdentityPlaceholderTitle />
                        )
                    }}
                    accept={acceptRules.identity}
                />
            )}
        />
        <Typography
            marginTop={2}
            variant='h6'
        >
            Copy of your license(s)
        </Typography>
        <OnboardingInputControl
            name={FormField.CopyOfLicencesFileKey}
            isIncomplete={isIncomplete}
            requestedChangeFields={requestedChangeFields}
            renderInput={({ disabled }) => (
                <ProofFileInput
                    multiple={false}
                    disabled={disabled}
                    name={FormField.CopyOfLicencesFileKey}
                    file={values[FormField.CopyOfLicencesFileKey]}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    placeholderProps={{
                        titleSlot: (
                            <IdentityPlaceholderTitle />
                        )
                    }}
                    accept={acceptRules.identity}
                />
            )}
        />
        <Typography
            marginTop={2}
            variant='h6'
        >
            Proof of Domain Ownership
        </Typography>
        <Typography
            variant='caption'
        >
            A monthly or yearly invoice or screenshot clearly showing ownership of each domain you wish to onboard
        </Typography>
        <OnboardingInputControl
            name={FormField.ProofOfDomainOwnershipFileKey}
            isIncomplete={isIncomplete}
            requestedChangeFields={requestedChangeFields}
            renderInput={({ disabled }) => (
                <ProofFileInput
                    multiple={false}
                    disabled={disabled}
                    name={FormField.ProofOfDomainOwnershipFileKey}
                    file={values[FormField.ProofOfDomainOwnershipFileKey]}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    placeholderProps={{
                        titleSlot: (
                            <IdentityPlaceholderTitle />
                        )
                    }}
                    accept={acceptRules.identity}
                />
            )}
        />
        <Typography
            marginTop={2}
            variant='h6'
        >
            Official Company Documents filed with the authorities confirming latest Directorship and Shareholding of the company
        </Typography>
        <OnboardingInputControl
            name={FormField.OfficialCompanyDocWithAuthorityFileKey}
            isIncomplete={isIncomplete}
            requestedChangeFields={requestedChangeFields}
            renderInput={({ disabled }) => (
                <ProofFileInput
                    multiple={false}
                    disabled={disabled}
                    name={FormField.OfficialCompanyDocWithAuthorityFileKey}
                    file={values[FormField.OfficialCompanyDocWithAuthorityFileKey]}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    placeholderProps={{
                        titleSlot: (
                            <IdentityPlaceholderTitle />
                        )
                    }}
                    accept={acceptRules.identity}
                />
            )}
        />
        <Typography
            marginTop={2}
            variant='h6'
        >
            Company structure chart / diagram
        </Typography>
        <Typography
            variant='caption'
        >
            Where the structure of the company involves three or more companies, please provide clear chart/diagram along with the same documents, as stated above, for each company and shareholder involved in the structure.
        </Typography>
        <OnboardingInputControl
            name={FormField.StructureChartFileKey}
            isIncomplete={isIncomplete}
            requestedChangeFields={requestedChangeFields}
            renderInput={({ disabled }) => (
                <ProofFileInput
                    multiple={false}
                    disabled={disabled}
                    name={FormField.StructureChartFileKey}
                    file={values[FormField.StructureChartFileKey]}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    placeholderProps={{
                        titleSlot: (
                            <IdentityPlaceholderTitle />
                        )
                    }}
                    accept={acceptRules.identity}
                />
            )}
        />
    </OnboardingForm>
);

export default UploadDocuments;
